import React, { useState } from 'react';
import './styles.scss';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { RoutesStatesAtom, RoutesStates } from '../../atoms/routesStates';
import { useAuth } from '../../context/authContext';
import { CustomerAtom } from '../../atoms/customerAtom';
import { useTheme } from '../../context/ThemeContext';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

export const Header: React.FC = () => {
  const [customer] = useRecoilState(CustomerAtom);
  const setUiState = useSetRecoilState(RoutesStatesAtom);
  const { userLogin } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDarkMode } = useTheme();

  function handleNavItemClick(route: RoutesStates): void {
    setUiState(route);
    setIsMobileMenuOpen(false);
  }

  return (
    <header className={`relative z-50 w-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
      {/* Auth Section - Absolute Positioned */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center pr-4">
        <div className="hidden md:flex items-center">
          {userLogin && customer ? (
            <a 
              href="#/profile" 
              onClick={() => handleNavItemClick(RoutesStates.PROFILE)}
              className="relative group"
            >
              <img
                alt="profile"
                src={customer?.profile_image as string}
                className="h-8 w-8 rounded-full ring-2 ring-indigo-600 transition-transform duration-200 transform group-hover:scale-105"
              />
              <span className="absolute -bottom-1 -right-1 h-2 w-2 bg-green-400 rounded-full border-2 border-white"></span>
            </a>
          ) : (
            <div className="flex items-center space-x-3">
              <a 
                href="#/login" 
                className={`text-base font-medium ${isDarkMode ? 'text-white hover:text-accent-primary' : 'text-gray-700 hover:text-indigo-600'} transition-colors duration-200`}
              >
                Log in
              </a>
              <a
                href="#/signup"
                className="rounded-full bg-indigo-600 px-3 py-1 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors duration-200"
              >
                Get Started
              </a>
            </div>
          )}
        </div>
      </div>

      <div className="flex h-20">
        {/* Left Section: Logo + Navigation */}
        <div className="flex items-center">
          {/* Logo Section */}
          <div className="flex-shrink-0">
            <div className="flex items-center cursor-pointer" onClick={() => window.location.href = '/'}>
              <img src="./tickertribe512.png" alt="Logo" className="h-10 w-auto sm:h-12" />
              <h1 className="ml-2 sm:ml-3 text-xl sm:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Ticker Tribe
              </h1>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex md:space-x-8 ml-8">
            <a 
              href="#/docs" 
              onClick={() => handleNavItemClick(RoutesStates.DOCS)}
              className={`inline-flex items-center px-1 pt-1 text-base font-medium ${isDarkMode ? 'text-white hover:text-accent-primary' : 'text-gray-700 hover:text-indigo-600'} transition-colors duration-200`}
            >
              Docs
            </a>
            <a 
              href="#/pricing" 
              onClick={() => handleNavItemClick(RoutesStates.PRICING)}
              className={`inline-flex items-center px-1 pt-1 text-base font-medium ${isDarkMode ? 'text-white hover:text-accent-primary' : 'text-gray-700 hover:text-indigo-600'} transition-colors duration-200`}
            >
              Pricing
            </a>
            <a 
              href="#/company" 
              onClick={() => handleNavItemClick(RoutesStates.COMPANY)}
              className={`inline-flex items-center px-1 pt-1 text-base font-medium ${isDarkMode ? 'text-white hover:text-accent-primary' : 'text-gray-700 hover:text-indigo-600'} transition-colors duration-200`}
            >
              Company
            </a>
            <a 
              href="#/contact" 
              onClick={() => handleNavItemClick(RoutesStates.CONTACT)}
              className={`inline-flex items-center px-1 pt-1 text-base font-medium ${isDarkMode ? 'text-white hover:text-accent-primary' : 'text-gray-700 hover:text-indigo-600'} transition-colors duration-200`}
            >
              Contact
            </a>
            <a 
              href="#/candlestick" 
              onClick={() => handleNavItemClick(RoutesStates.CANDLESTICK)}
              className={`inline-flex items-center px-1 pt-1 text-base font-medium ${isDarkMode ? 'text-white hover:text-accent-primary' : 'text-gray-700 hover:text-indigo-600'} transition-colors duration-200`}
            >
              Chart
            </a>
            {userLogin && customer && (
              <a 
                href="#/download" 
                onClick={() => handleNavItemClick(RoutesStates.DOWNLOAD)}
                className={`inline-flex items-center px-1 pt-1 text-base font-medium ${isDarkMode ? 'text-white hover:text-accent-primary' : 'text-gray-700 hover:text-indigo-600'} transition-colors duration-200`}
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-1" />
                Download CSV
              </a>
            )}
          </nav>
        </div>

        {/* Mobile menu button */}
        <div className="ml-auto flex md:hidden">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:bg-gray-200 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 transition-colors duration-200"
            aria-label="Toggle navigation menu"
          >
            <span className="sr-only">Open main menu</span>
            {isMobileMenuOpen ? (
              <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu - Left Side Drawer */}
      <div 
        className={`md:hidden fixed inset-0 z-50 ${isMobileMenuOpen ? 'block' : 'hidden'}`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        {/* Backdrop */}
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" />
        
        {/* Drawer */}
        <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl flex flex-col">
          {/* Drawer Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center">
              <img src="./tickertribe512.png" alt="Logo" className="h-8 w-auto" />
              <h2 className="ml-2 text-lg font-semibold">Menu</h2>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="rounded-md p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            >
              <span className="sr-only">Close menu</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 px-2 py-4">
            <div className="space-y-1">
              <a
                href="#/docs"
                onClick={() => handleNavItemClick(RoutesStates.DOCS)}
                className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
              >
                Docs
              </a>
              <a
                href="#/pricing"
                onClick={() => handleNavItemClick(RoutesStates.PRICING)}
                className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
              >
                Pricing
              </a>
              <a
                href="#/company"
                onClick={() => handleNavItemClick(RoutesStates.COMPANY)}
                className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
              >
                Company
              </a>
              <a
                href="#/contact"
                onClick={() => handleNavItemClick(RoutesStates.CONTACT)}
                className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
              >
                Contact
              </a>
              <a
                href="#/candlestick"
                onClick={() => handleNavItemClick(RoutesStates.CANDLESTICK)}
                className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
              >
                Chart
              </a>
              {userLogin && customer && (
                <a
                  href="#/download"
                  onClick={() => handleNavItemClick(RoutesStates.DOWNLOAD)}
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
                >
                  <div className="flex items-center">
                    <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                    Download CSV
                  </div>
                </a>
              )}
            </div>
          </nav>

          {/* Auth Section */}
          {userLogin && customer ? (
            <div className="p-4 border-t mt-auto">
              <a
                href="#/profile"
                onClick={() => handleNavItemClick(RoutesStates.PROFILE)}
                className="flex items-center space-x-3 px-3 py-2 text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
              >
                <img
                  alt="profile"
                  src={customer?.profile_image as string}
                  className="h-8 w-8 rounded-full ring-2 ring-indigo-600"
                />
                <span className="text-base font-medium">Profile</span>
                <span className="h-2 w-2 bg-green-400 rounded-full border-2 border-white"></span>
              </a>
            </div>
          ) : (
            <div className="p-4 border-t mt-auto">
              <div className="space-y-2">
                <a
                  href="#/login"
                  className="block w-full px-3 py-2 text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 rounded-md text-center"
                >
                  Log in
                </a>
                <a
                  href="#/signup"
                  className="block w-full px-3 py-2 text-base font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-md text-center"
                >
                  Get Started
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
